import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { ErrorInfo } from '../../classes/client';
import FormContainer from '../layout/form-container';
import { useFetchMapById } from '../../classes/middleware';
import {
  Designer,
  TextExporterFactory,
  ImageExporterFactory,
  Exporter,
  Mindmap,
  SizeType,
} from '@wisemapping/editor';
import { fetchMindmap } from '../editor-page/PersistenceManagerUtils';

type ExportFormat = 'svg' | 'jpg' | 'png' | 'txt' | 'mm' | 'wxml' | 'md';
type ExportGroup = 'image' | 'document' | 'mindmap-tool';

const ExportMap = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  const mapId = id ? parseInt(id, 10) : null;
  const intl = useIntl();

  const [exportGroup, setExportGroup] = useState<ExportGroup>('image');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('svg');
  const [zoomToFit, setZoomToFit] = useState<boolean>(true);
  const [exportError, setExportError] = useState<ErrorInfo>();
  const [submit, setSubmit] = useState<boolean>(false);

  // Fetch map info
  const { data: map, isLoading, error: mapError } = useFetchMapById(mapId || 0);

  const handleOnExportFormatChange = (event) => {
    setExportFormat(event.target.value);
  };

  const handleOnGroupChange = (event) => {
    const value: ExportGroup = event.target.value;
    setExportGroup(value);

    let defaultFormat: ExportFormat;
    switch (value) {
      case 'document':
        defaultFormat = 'txt';
        break;
      case 'image':
        defaultFormat = 'svg';
        break;
      case 'mindmap-tool':
        defaultFormat = 'wxml';
        break;
    }
    setExportFormat(defaultFormat);
  };

  const handleOnZoomToFit = (): void => {
    setZoomToFit(!zoomToFit);
  };

  const handleExport = (): void => {
    setSubmit(true);
  };

  const exporter = async (formatType: ExportFormat): Promise<string> => {
    let svgElement: Element | undefined;
    let size: SizeType | undefined;
    let mindmap: Mindmap;

    const designer: Designer = globalThis.designer;

    // Handle image exports differently when not in the editor
    if (formatType === 'svg' || formatType === 'png' || formatType === 'jpg') {
      if (!designer) {
        // When not in the editor, use direct API endpoints for image exports
        if (!mapId) {
          throw new Error('Map ID is required for export');
        }

        const baseUrl = window.location.origin;
        let endpoint;

        if (formatType === 'svg') {
          endpoint = `/api/restful/maps/${mapId}/svg`;
        } else if (formatType === 'png') {
          endpoint = `/api/restful/maps/${mapId}/png`;
        } else if (formatType === 'jpg') {
          endpoint = `/api/restful/maps/${mapId}/jpg`;
        }

        return `${baseUrl}${endpoint}`;
      } else {
        // In the editor, use the ImageExporterFactory
        const workspace = designer.getWorkSpace();
        svgElement = workspace.getSVGElement();
        size = { width: window.innerWidth, height: window.innerHeight };

        const exporter = ImageExporterFactory.create(
          formatType,
          svgElement,
          size.width,
          size.height,
          zoomToFit,
        );

        return exporter.exportAndEncode();
      }
    } else {
      // For non-image exports (text, mindmap formats)
      if (designer) {
        mindmap = designer.getMindmap();
      } else {
        if (!mapId) {
          throw new Error('Map ID is required for export');
        }
        mindmap = await fetchMindmap(mapId);
      }

      const exporter = TextExporterFactory.create(formatType, mindmap);
      return exporter.exportAndEncode();
    }
  };

  useEffect(() => {
    if (submit) {
      exporter(exportFormat)
        .then((url: string) => {
          // Create hidden anchor to force download ...
          const anchor: HTMLAnchorElement = document.createElement('a');
          anchor.style.display = 'display: none';
          anchor.download = `${map?.title}.${exportFormat}`;
          anchor.href = url;
          document.body.appendChild(anchor);

          // Trigger click ...
          anchor.click();

          // Clean up ...
          URL.revokeObjectURL(url);
          document.body.removeChild(anchor);

          // Reset submit state
          setSubmit(false);
        })
        .catch((fail) => {
          console.error('Unexpected error during export:' + fail);
          setExportError({
            msg: `Export failed: ${fail.message || 'Unknown error'}`
          });
          setSubmit(false);
        });
    }
  }, [submit, map, exportFormat]);

  return (
    <div>
      <FormContainer>
        <h1>
          <FormattedMessage id="export.title1" defaultMessage="Exporter la map" />
        </h1>

        {exportError && (
          <Alert severity="error" style={{ marginBottom: '20px' }}>
            <p>{exportError.msg}</p>
          </Alert>
        )}

        {isLoading && (
          <Alert severity="info">
            {/*<FormattedMessage id="loading.map" defaultMessage="Loading map..." />*/}
            <FormattedMessage id="loading.map1" defaultMessage="Chargement de la map..." />
          </Alert>
        )}

        {mapError && (
          <Alert severity="error">
            {/*<FormattedMessage id="error.loading.map" defaultMessage="Error loading map" />*/}
            <FormattedMessage id="error.loading.map1" defaultMessage="Erreur lors du chargement de la map" />
          </Alert>
        )}

        {!isLoading && !mapError && map && (
          <Alert severity="info" style={{ marginBottom: '20px' }}>
            <p>
              {/*<FormattedMessage
                id="export.map.info"
                defaultMessage="Exporting map: {title}"
                values={{ title: map.title }}
              />*/}
              <FormattedMessage
                id="export.map.info1"
                defaultMessage="Exportation de la map: {title}"
                values={{ title: map.title }}
              />
            </p>
          </Alert>
        )}

        {/*<div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <FormattedMessage
            id="export.desc"
            defaultMessage="Export this map in the format that you want and start using it in your presentations or sharing by email"
          />
        </div>*/}

        <FormControl component="fieldset" fullWidth>
          <RadioGroup name="export" value={exportGroup} onChange={handleOnGroupChange}>
            {/*<FormControl style={{ marginBottom: '15px' }}>
              <FormControlLabel
                value="image"
                control={<Radio color="primary" />}
                label={intl.formatMessage({
                  id: 'export.image',
                  defaultMessage:
                    'Image: Get a graphic representation of your map including all colors and shapes.',
                })}
                style={{ marginBottom: '10px' }}
              />
              {exportGroup === 'image' && (
                <>
                  <Select
                    onChange={handleOnExportFormatChange}
                    variant="outlined"
                    value={exportFormat}
                    fullWidth
                    style={{ marginBottom: '10px' }}
                  >
                    <MenuItem value="svg">
                      Scalable Vector Graphics (SVG)
                    </MenuItem>
                    <MenuItem value="png">
                      Portable Network Graphics (PNG)
                    </MenuItem>
                    <MenuItem value="jpg">
                      JPEG Image (JPEG)
                    </MenuItem>
                  </Select>
                  <FormControlLabel
                    control={<Checkbox checked={zoomToFit} onChange={handleOnZoomToFit} />}
                    label={intl.formatMessage({
                      id: 'export.img-center',
                      defaultMessage: 'Center and zoom to fit',
                    })}
                  />
                </>
              )}
            </FormControl>*/}

            <FormControl style={{ marginBottom: '15px' }}>
              <FormControlLabel
                value="document"
                control={<Radio style={{ color: '#82b440' }} />}
                label={intl.formatMessage({
                  id: 'export.document-label1',
                  defaultMessage:
                    'Document : Exportez votre map mentale dans un document autonome prêt à être partagé',
                })}
                style={{ marginBottom: '10px' }}
              />
              {exportGroup === 'document' && (
                <Select
                  onChange={handleOnExportFormatChange}
                  variant="outlined"
                  value={exportFormat}
                  fullWidth
                  sx={{
                    color: 'black', // Text color inside the Select
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'gray', // Default border color
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#82b440', // Border on hover
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#82b440', // Border when focused
                    },
                    '& .MuiSelect-icon': {
                      color: 'black', // Dropdown icon color
                    },
                  }}
                >
                  <MenuItem value="txt">Fichier texte brut (TXT)</MenuItem>
                  <MenuItem value="md">Markdown (MD)</MenuItem>
                </Select>
              )}
            </FormControl>

            <FormControl style={{ marginBottom: '15px' }}>
              <FormControlLabel
                value="mindmap-tool"
                control={<Radio style={{ color: '#82b440' }} />}
                label={intl.formatMessage({
                  id: 'export.document1',
                  defaultMessage:
                    'Outils de map mentale : Exportez votre map mentale dans des formats compatibles avec des outils tiers',
                })}
                style={{ marginBottom: '10px' }}
              />
              {exportGroup === 'mindmap-tool' && (
                <Select
                  onChange={handleOnExportFormatChange}
                  variant="outlined"
                  value={exportFormat}
                  fullWidth
                  sx={{
                    color: 'black', // Text color inside the Select
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'gray', // Default border color
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#82b440', // Border on hover
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#82b440', // Border when focused
                    },
                    '& .MuiSelect-icon': {
                      color: 'black', // Dropdown icon color
                    },
                  }}
                >
                  <MenuItem value="wxml">
                    WiseMapping (WXML)
                  </MenuItem>
                  <MenuItem value="mm">
                    Freemind 1.0.1 (MM)
                  </MenuItem>
                </Select>
              )}
            </FormControl>
          </RadioGroup>
        </FormControl>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            style={{ margin: '10px 5px', width: '100%', backgroundColor: '#82b440' }}
            disabled={!mapId || isLoading || !!mapError}
          >
            <FormattedMessage id="export.title1" defaultMessage="Exporter" />
          </Button>
        </div>
      </FormContainer>
    </div>
  );
};

export default ExportMap;